import { AxiosError } from 'axios';
import { ApiError } from './api-error';

export const handleApiError = (error: unknown, language: string = 'ar'): ApiError => {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status || 500;
    const responseData = error.response?.data;
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new ApiError(
        'Request timed out. Please check your internet connection and try again.', 
        408, 
        'TIMEOUT_ERROR'
      );
    }
    
    // Handle network errors
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return new ApiError(
        'Network error. Please check your internet connection.', 
        0, 
        'NETWORK_ERROR'
      );
    }
    
    // Handle validation errors (422)
    if (status === 422 && responseData?.errors) {
      const errors = responseData.errors;
      // Format all error messages
      const errorMessages = Object.entries(errors)
        .map(([field, messages]) => {
          const msgs = Array.isArray(messages) ? messages : [messages];
          return `${field}: ${msgs.join(', ')}`;
        })
        .join('\n');
      
      // Get first error message for simple display
      const firstErrorKey = Object.keys(errors)[0];
      const firstErrorMessage = Array.isArray(errors[firstErrorKey]) 
        ? errors[firstErrorKey][0] 
        : errors[firstErrorKey];
      
      const message = firstErrorMessage || responseData?.message || 'Validation error';
      const apiError = new ApiError(message, status, 'VALIDATION_ERROR', responseData);
      // Store all errors for detailed display
      (apiError as any).allErrors = errors;
      (apiError as any).formattedErrors = errorMessages;
      return apiError;
    }
    
    // Handle specific tender deadline error
    if (responseData?.code === 'TENDER_DEADLINE_PASSED') {
      const tenderTitle = responseData?.details?.tender_title;
      const titleText = typeof tenderTitle === 'string' 
        ? tenderTitle 
        : tenderTitle?.ar || tenderTitle?.en || '';
      
      const message = language === 'ar' 
        ? `لقد انتهى الموعد النهائي لتقديم العروض على المناقصة "${titleText}". يرجى اختيار مناقصة أخرى متاحة.`
        : `The deadline to submit offers for tender "${titleText}" has passed. Please choose another available tender.`;
      
      return new ApiError(
        message,
        status,
        responseData.code,
        responseData
      );
    }
    
    const message =
      responseData?.message ||
      error.message ||
      'حدث خطأ غير متوقع';
    const code = responseData?.code;
    const data = responseData;

    return new ApiError(message, status, code, data);
  }

  if (error instanceof Error) {
    return new ApiError(error.message, 500);
  }

  return new ApiError('حدث خطأ غير متوقع', 500);
};

export const getErrorMessage = (error: unknown, language: string = 'ar'): string => {
  const apiError = handleApiError(error, language);
  return apiError.message;
};

export const getAllValidationErrors = (error: unknown): Record<string, string[]> | null => {
  const apiError = handleApiError(error);
  if (apiError.status === 422 && (apiError as any).allErrors) {
    return (apiError as any).allErrors;
  }
  return null;
};

export const getFormattedErrors = (error: unknown): string | null => {
  const apiError = handleApiError(error);
  if (apiError.status === 422 && (apiError as any).formattedErrors) {
    return (apiError as any).formattedErrors;
  }
  return null;
};
